import { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showErrorDetails: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, showErrorDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Scenario Designer Error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div style={{
          width: '100%',
          height: '100%',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff'
        }}>
          <div style={{
            maxWidth: '600px',
            padding: '40px',
            backgroundColor: '#fee',
            border: '2px solid #fcc',
            borderRadius: '8px',
            color: '#c00',
            position: 'relative'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 'bold' }}>
              Unable to render the scenario editor
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', lineHeight: '1.5' }}>
              Please try reloading the page or contact support if the issue persists.
            </p>
            {this.state.error && (
              <>
                <button
                  onClick={() => this.setState({ showErrorDetails: true })}
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '6px 12px',
                    backgroundColor: '#fff',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c00'
                  }}
                >
                  Error details
                </button>
                {this.state.showErrorDetails && (
                  <div
                    onClick={() => this.setState({ showErrorDetails: false })}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '8px',
                        maxWidth: '800px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        position: 'relative',
                        border: '2px solid #fcc'
                      }}
                    >
                      <button
                        onClick={() => this.setState({ showErrorDetails: false })}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          cursor: 'pointer',
                          fontSize: '20px',
                          padding: '4px 12px',
                          backgroundColor: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          color: '#666'
                        }}
                      >
                        ×
                      </button>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 'bold', color: '#c00' }}>
                        Error Details
                      </h4>
                      <pre style={{
                        fontSize: '12px',
                        overflow: 'auto',
                        backgroundColor: '#f5f5f5',
                        padding: '10px',
                        borderRadius: '4px',
                        maxHeight: '60vh',
                        margin: 0,
                        color: '#333'
                      }}>
                        {this.state.error.message}
                        {'\n\n'}
                        {this.state.error.stack}
                      </pre>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
